/**
 * Evaluator utilities for scoring answers
 */

/**
 * Score a multiple choice question
 * @param {string} answer - User's answer
 * @param {string} correct - Correct answer
 * @returns {number} Score (1 for correct, 0 for incorrect)
 */
export const scoreMCQ = (answer, correct) => {
  return answer === correct ? 1 : 0
}

/**
 * Evaluate a text answer against a rubric
 * @param {string} text - User's answer text
 * @param {Object} rubric - Rubric containing keywords and structure elements
 * @returns {Object} Evaluation result
 */
export const evaluateTextAnswer = (text, rubric) => {
  if (!text || typeof text !== 'string') {
    return {
      score: 0,
      highlights: [],
      missingKeywords: rubric.keywords || [],
      feedback: 'No answer provided',
    }
  }

  const normalizedText = text.toLowerCase()
  const keywords = rubric.keywords || []
  const foundKeywords = []
  const missingKeywords = []

  // Check for keywords
  keywords.forEach((keyword) => {
    if (normalizedText.includes(keyword.toLowerCase())) {
      foundKeywords.push(keyword)
    } else {
      missingKeywords.push(keyword)
    }
  })

  // Calculate keyword score (0-50%)
  const keywordScore = keywords.length > 0 
    ? (foundKeywords.length / keywords.length) * 50 
    : 25

  // Check for structure elements (if applicable)
  let structureScore = 0
  if (rubric.structure_elements) {
    const structureElements = rubric.structure_elements
    let foundElements = 0

    structureElements.forEach((element) => {
      if (normalizedText.includes(element.toLowerCase())) {
        foundElements++
      }
    })

    structureScore = (foundElements / structureElements.length) * 30
  } else {
    structureScore = 15 // Default structure score if not applicable
  }

  // Length score (0-20%)
  const wordCount = text.trim().split(/\s+/).length
  const lengthScore = Math.min((wordCount / 100) * 20, 20) // Max 20% for 100+ words

  const totalScore = Math.round(keywordScore + structureScore + lengthScore)

  return {
    score: Math.min(totalScore, 100),
    highlights: foundKeywords,
    missingKeywords,
    feedback: generateFeedback(totalScore, foundKeywords, missingKeywords),
    details: {
      keywordScore: Math.round(keywordScore),
      structureScore: Math.round(structureScore),
      lengthScore: Math.round(lengthScore),
      wordCount,
    },
  }
}

/**
 * Auto-score a TAT story
 * @param {string} text - User's story text
 * @param {string} exampleText - Example story (optional)
 * @param {Object} rubric - Scoring rubric
 * @returns {Object} Evaluation result
 */
export const autoScoreTAT = (text, exampleText, rubric) => {
  if (!text || typeof text !== 'string') {
    return {
      score: 0,
      feedback: 'No story provided',
      suggestions: ['Write a complete story with clear situation, action, and result'],
    }
  }

  const normalizedText = text.toLowerCase()
  const wordCount = text.trim().split(/\s+/).length
  const sentences = text.split(/[.!?]+/).filter((s) => s.trim().length > 0)

  // Check for STAR structure markers
  const structureMarkers = ['situation', 'task', 'action', 'result']
  const foundStructure = structureMarkers.filter((marker) =>
    normalizedText.includes(marker.toLowerCase())
  )

  // Check for positive themes
  const positiveThemes = rubric.positive_themes || []
  const foundPositiveThemes = positiveThemes.filter((theme) =>
    normalizedText.includes(theme.toLowerCase())
  )

  // Check for negative themes (deduct points)
  const negativeThemes = rubric.negative_themes || []
  const foundNegativeThemes = negativeThemes.filter((theme) =>
    normalizedText.includes(theme.toLowerCase())
  )

  // Scoring components
  let score = 0
  const suggestions = []

  // Length score (0-20 points)
  if (wordCount < 50) {
    score += wordCount * 0.2
    suggestions.push('Story is too short. Aim for 100-150 words.')
  } else if (wordCount < 100) {
    score += 10 + (wordCount - 50) * 0.2
    suggestions.push('Good length, but could be more detailed.')
  } else if (wordCount <= 200) {
    score += 20
  } else {
    score += 15
    suggestions.push('Story is quite long. Be more concise.')
  }

  // Structure score (0-30 points)
  const structureScore = (foundStructure.length / structureMarkers.length) * 30
  score += structureScore
  if (structureScore < 15) {
    suggestions.push('Include clear Situation, Task, Action, and Result in your story.')
  }

  // Theme score (0-30 points)
  const themeScore = positiveThemes.length > 0
    ? (foundPositiveThemes.length / positiveThemes.length) * 30
    : 15
  score += themeScore
  if (themeScore < 15) {
    suggestions.push(`Include positive themes like: ${positiveThemes.slice(0, 3).join(', ')}`)
  }

  // Coherence score (0-20 points) - based on sentence count and structure
  const coherenceScore = sentences.length >= 4 && sentences.length <= 12 ? 20 : 10
  score += coherenceScore
  if (coherenceScore < 15) {
    suggestions.push('Ensure your story flows logically with proper sentences.')
  }

  // Deduct for negative themes
  const negativeDeduction = foundNegativeThemes.length * 5
  score -= negativeDeduction
  if (negativeDeduction > 0) {
    suggestions.push('Avoid overly negative themes. Focus on positive outcomes and problem-solving.')
  }

  const finalScore = Math.max(0, Math.min(100, Math.round(score)))

  return {
    score: finalScore,
    feedback: generateTATFeedback(finalScore),
    suggestions,
    details: {
      wordCount,
      sentenceCount: sentences.length,
      foundStructure,
      foundPositiveThemes,
      foundNegativeThemes,
    },
  }
}

/**
 * Generate feedback based on score
 * @param {number} score - The score
 * @param {Array} foundKeywords - Keywords found in answer
 * @param {Array} missingKeywords - Keywords missing from answer
 * @returns {string} Feedback message
 */
const generateFeedback = (score, foundKeywords, missingKeywords) => {
  if (score >= 80) {
    return 'Excellent answer! You covered most key points.'
  } else if (score >= 60) {
    return `Good answer. Consider including: ${missingKeywords.slice(0, 2).join(', ')}`
  } else if (score >= 40) {
    return `Fair answer. Missing key concepts: ${missingKeywords.slice(0, 3).join(', ')}`
  } else {
    return `Needs improvement. Focus on: ${missingKeywords.slice(0, 3).join(', ')}`
  }
}

/**
 * Generate TAT-specific feedback
 * @param {number} score - The score
 * @returns {string} Feedback message
 */
const generateTATFeedback = (score) => {
  if (score >= 80) {
    return 'Excellent story! Clear structure, positive themes, and good length.'
  } else if (score >= 60) {
    return 'Good story with room for improvement in structure or themes.'
  } else if (score >= 40) {
    return 'Fair attempt. Work on including clear STAR structure and positive resolution.'
  } else {
    return 'Needs significant improvement. Focus on complete stories with positive outcomes.'
  }
}

/**
 * Compare user answer with ideal answer (similarity check)
 * @param {string} userAnswer - User's answer
 * @param {string} idealAnswer - Ideal answer
 * @returns {number} Similarity score (0-100)
 */
export const calculateSimilarity = (userAnswer, idealAnswer) => {
  if (!userAnswer || !idealAnswer) return 0

  const normalize = (text) =>
    text
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/)
      .filter((w) => w.length > 3) // Filter out short words

  const userWords = normalize(userAnswer)
  const idealWords = normalize(idealAnswer)

  const commonWords = userWords.filter((word) => idealWords.includes(word))
  const similarity = (commonWords.length / Math.max(idealWords.length, 1)) * 100

  return Math.round(similarity)
}
